import { BareButton } from '@calendly/ui/components/bare-button';
import { BrandLogo } from '@calendly/ui/components/brand-logo';
import { Button } from '@calendly/ui/components/button';
import { AtmCardIcon, CheckCircleIcon, CloseMiniIcon } from '@calendly/ui/components/icons';
import { BodyText } from '@calendly/ui/components/typography';
import { colorIconSuccess, colorTextStandard } from '@calendly/ui/theme';
import classNames from 'classnames';
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { redirectToAuthorizeUrl } from 'actions/omniauth';
import { useGetEventTypesCollectionQuery, useGetPaidBookingStatusQuery, useGetUserQuery, useUpdateUserGrowthExperimentsMutation } from 'common';
import { useGetReferralQuery as useGetPaypalReferralQuery } from 'components/integrations/paypal/api';
import { useIsPaypalPpcpFeatureVariation } from 'features/integrations';
import { TRACKING_EVENT_NAMES, useTrack, useTrackOnce } from 'hooks/analytics_events';
const ONBOARDING_PAYMENTS_SETUP_CHECKLIST_EXPERIMENT_KEY = 'growth_onboarding_payments_setup_checklist';
export const ONBOARDING_PAYMENTS_SETUP_CHECKLIST_VARIANTS = {
  CONTROL: 'control',
  VARIANT_1: 'variant_1',
  VARIANT_2: 'variant_2',
  VARIANT_3: 'variant_3'
};
const PROGRESS_STATES = {
  NOT_CONNECTED: 'not_connected',
  CONNECTED: 'connected',
  ENABLED: 'enabled',
  SHARED: 'shared',
  PAID: 'paid'
};
const PROGRESS_RANK = {
  [PROGRESS_STATES.NOT_CONNECTED]: 0,
  [PROGRESS_STATES.CONNECTED]: 1,
  [PROGRESS_STATES.ENABLED]: 2,
  [PROGRESS_STATES.SHARED]: 3,
  [PROGRESS_STATES.PAID]: 4
};
const PAYMENT_PROVIDER_NAMES = ['stripe', 'paypal', 'paypal_ppcp'];
const PAYPAL_CONNECT_URL_FALLBACK = '/integrations/paypal';
const PAYPAL_PPCP_CONNECTOR = {
  authorizePath: '/users/auth/paypal_ppcp',
  ctaName: 'payments_onboarding_setup_checklist_connect_paypal',
  label: 'PayPal',
  logoName: 'pay-pal',
  provider: 'paypal_ppcp'
};
const PAYPAL_CONNECTOR = {
  ctaName: 'payments_onboarding_setup_checklist_connect_paypal',
  label: 'PayPal',
  logoName: 'pay-pal',
  provider: 'paypal'
};
const STRIPE_CONNECTOR = {
  authorizePath: '/users/auth/stripe_oauth2',
  ctaName: 'payments_onboarding_setup_checklist_connect_stripe',
  label: 'Stripe',
  logoName: 'stripe',
  provider: 'stripe'
};
const MAX_BANNER_DISMISSALS = 5;
const bannerCss = "bfifezg";
const bannerMainCss = "bo86658";
const bannerContentCss = "b12jqok0";
const bannerTitleCss = "bpq1087";
const stepsCss = "ssxnod8";
const stepCss = "sw838v5";
const stepInactiveCss = "si4n1fu";
const stepCompleteCss = "s1v52md";
const stepNumberCss = "s1m327eg";
const separatorCss = "s8f5wz8";
const bannerActionsCss = "b1nk323d";
const closeButtonCss = "c1artp64";
const providerLogoCss = "pyz1x95";
const providerSortOrder = {
  stripe: 0,
  paypal: 1,
  paypal_ppcp: 1
};
const getOnboardingPaymentsSetupChecklistExperimentState = userData => userData?.growth_experiments?.[ONBOARDING_PAYMENTS_SETUP_CHECKLIST_EXPERIMENT_KEY] || {};
const getOnboardingPaymentsSetupChecklistVariant = userData => getOnboardingPaymentsSetupChecklistExperimentState(userData).variant;
const isOnboardingPaymentsSetupChecklistEligible = userData => {
  const state = getOnboardingPaymentsSetupChecklistExperimentState(userData);
  return state.intro_flow_exposed === true && [ONBOARDING_PAYMENTS_SETUP_CHECKLIST_VARIANTS.VARIANT_1, ONBOARDING_PAYMENTS_SETUP_CHECKLIST_VARIANTS.VARIANT_2, ONBOARDING_PAYMENTS_SETUP_CHECKLIST_VARIANTS.VARIANT_3].includes(state.variant);
};
export const isOnboardingPaymentsSetupChecklistVariant = (userData, variants) => {
  if (!isOnboardingPaymentsSetupChecklistEligible(userData)) return false;
  const allowedVariants = Array.isArray(variants) ? variants : [variants];
  return allowedVariants.includes(getOnboardingPaymentsSetupChecklistVariant(userData));
};
export const isManageablePaymentEventType = eventType => Boolean(eventType?.policy?.edit) && ['User', 'Page'].includes(eventType?.owner_type) && !eventType?.parent_id && !eventType?.is_user_group_managed;
const getPaymentProviderLabel = provider => {
  if (provider === 'stripe') return 'Stripe';
  if (provider === 'paypal' || provider === 'paypal_ppcp') return 'PayPal';
  return null;
};
const formatPaymentAmount = payment => {
  if (!Array.isArray(payment)) return null;
  const [amountCents, currency = 'USD'] = payment;
  const amount = Number(amountCents);
  if (!Number.isFinite(amount)) return null;
  const hasMinorUnits = amount % 100 !== 0;
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: hasMinorUnits ? 2 : 0,
    maximumFractionDigits: 2
  }).format(amount / 100);
};
export const getPaymentMetadataLabel = eventType => {
  const amount = formatPaymentAmount(eventType?.payment);
  const provider = getPaymentProviderLabel(eventType?.payment_provider);
  if (!amount || !provider) return null;
  return `${amount} via ${provider}`;
};
const getPaymentEnabled = eventType => Boolean(eventType?.has_active_payment_method && getPaymentMetadataLabel(eventType));
const getExperimentUpdate = (userData, data) => ({
  [ONBOARDING_PAYMENTS_SETUP_CHECKLIST_EXPERIMENT_KEY]: {
    ...getOnboardingPaymentsSetupChecklistExperimentState(userData),
    ...data
  }
});
const getConnectedPaymentProvider = integrations => (integrations || []).filter(integration => PAYMENT_PROVIDER_NAMES.includes(integration.name) && integration.connected).sort((left, right) => providerSortOrder[left.name] - providerSortOrder[right.name])[0]?.name;
const getEventTypes = data => (data?.results || []).flatMap(result => result.event_types || []).filter(Boolean);
const getProgressState = ({
  connectedPaymentProvider,
  hasPaidEventType,
  hasSharedPaidEventType,
  hasSuccessfulPaidBooking
}) => {
  if (hasSuccessfulPaidBooking) return PROGRESS_STATES.PAID;
  if (hasSharedPaidEventType) return PROGRESS_STATES.SHARED;
  if (hasPaidEventType) return PROGRESS_STATES.ENABLED;
  if (connectedPaymentProvider) return PROGRESS_STATES.CONNECTED;
  return PROGRESS_STATES.NOT_CONNECTED;
};
const getBannerCopy = (progressState, connectedPaymentProvider) => {
  const providerLabel = getPaymentProviderLabel(connectedPaymentProvider) || 'Stripe or PayPal';
  if (progressState === PROGRESS_STATES.PAID) return "You've collected your first payment!";
  if (progressState === PROGRESS_STATES.SHARED) {
    return 'Now, customers will pay before booking with you for this event type.';
  }
  if (progressState === PROGRESS_STATES.ENABLED) {
    return 'You enabled payments on your event type - now share your booking link';
  }
  if (progressState === PROGRESS_STATES.CONNECTED) {
    return `${providerLabel} is now connected - now enable a paid event`;
  }
  return 'Connect Stripe or PayPal to seamlessly collect payments';
};
const getBannerDismissedToday = dismissedAt => {
  if (!dismissedAt) return false;
  const dismissedDate = new Date(dismissedAt);
  if (Number.isNaN(dismissedDate.getTime())) return false;
  const today = new Date();
  return dismissedDate.getFullYear() === today.getFullYear() && dismissedDate.getMonth() === today.getMonth() && dismissedDate.getDate() === today.getDate();
};
const getShouldShowBanner = (experimentState, progressState) => {
  const dismissedCount = Number(experimentState.banner_dismissals_count || 0);
  if (dismissedCount >= MAX_BANNER_DISMISSALS) return false;
  const dismissedProgressState = experimentState.banner_dismissed_progress_state;
  const progressAdvanced = PROGRESS_RANK[progressState] > PROGRESS_RANK[dismissedProgressState];
  if (progressAdvanced) return true;
  return !getBannerDismissedToday(experimentState.banner_dismissed_at);
};
const getSteps = progressState => {
  const progressRank = PROGRESS_RANK[progressState];
  return [{
    rank: 1,
    completeLabel: 'Connected',
    activeLabel: 'Connect',
    pendingLabel: 'Connect'
  }, {
    rank: 2,
    completeLabel: 'Enabled',
    activeLabel: 'Enable',
    pendingLabel: 'Enable'
  }, {
    rank: 3,
    completeLabel: 'Shared',
    activeLabel: 'Share',
    pendingLabel: 'Share'
  }, {
    rank: 4,
    completeLabel: 'Got paid',
    activeLabel: 'Get paid',
    pendingLabel: 'Get paid'
  }].map(step => ({
    ...step,
    isComplete: progressRank >= step.rank,
    isActive: progressRank + 1 === step.rank
  }));
};
const getPaymentProviderConnectors = ({
  isPaypalPpcpFeatureVariation,
  paypalConnectUrl,
  isPaypalConnectUrlLoading
}) => [isPaypalPpcpFeatureVariation ? PAYPAL_PPCP_CONNECTOR : {
  ...PAYPAL_CONNECTOR,
  href: paypalConnectUrl || PAYPAL_CONNECT_URL_FALLBACK,
  isLoading: isPaypalConnectUrlLoading
}, STRIPE_CONNECTOR];
const ChecklistStep = ({
  step,
  index
}) => {
  let label = step.pendingLabel;
  if (step.isComplete) {
    label = step.completeLabel;
  } else if (step.isActive) {
    label = step.activeLabel;
  }
  return <>
      {index > 0 ? <span className={separatorCss} aria-hidden /> : null}
      <span className={classNames(stepCss, {
      [stepCompleteCss]: step.isComplete,
      [stepInactiveCss]: !step.isComplete && !step.isActive
    })}>
        {step.isComplete ? <CheckCircleIcon color={colorIconSuccess} /> : <span className={stepNumberCss}>{step.rank}</span>}
        <span>{label}</span>
      </span>
    </>;
};
const ProgressSteps = ({
  progressState
}) => <div className={stepsCss} aria-label="Payments setup progress">
    {getSteps(progressState).map((step, index) => <ChecklistStep key={step.rank} step={step} index={index} />)}
  </div>;
const getFirstEditablePaymentEventType = eventTypes => {
  const manageableEventTypes = eventTypes.filter(eventType => isManageablePaymentEventType(eventType) && eventType.active !== false);
  return manageableEventTypes.find(eventType => !getPaymentEnabled(eventType)) || manageableEventTypes[0];
};
const useEmptyConnectedIntegrationsQuery = () => ({
  data: [],
  isLoading: false
});
export const useRecordOnboardingPaymentsPaidEventTypeShared = ({
  eventType,
  parent
}) => {
  const {
    data: userData = {}
  } = useGetUserQuery();
  const [updateGrowthExperiments] = useUpdateUserGrowthExperimentsMutation();
  return useCallback(() => {
    if (parent !== 'event_type_card') return;
    if (!isOnboardingPaymentsSetupChecklistVariant(userData, [ONBOARDING_PAYMENTS_SETUP_CHECKLIST_VARIANTS.VARIANT_2, ONBOARDING_PAYMENTS_SETUP_CHECKLIST_VARIANTS.VARIANT_3])) return;
    if (!isManageablePaymentEventType(eventType) || !getPaymentEnabled(eventType)) return;
    updateGrowthExperiments(getExperimentUpdate(userData, {
      shared_paid_event_type_id: eventType.id,
      shared_paid_event_type_at: new Date().toISOString()
    }));
  }, [eventType, parent, updateGrowthExperiments, userData]);
};
export const OnboardingPaymentsSetupChecklist = ({
  onEnableEventTypePayment,
  useConnectedIntegrationsQuery = useEmptyConnectedIntegrationsQuery
}) => {
  const {
    data: userData,
    isLoading: isUserLoading
  } = useGetUserQuery();
  if (isUserLoading || !isOnboardingPaymentsSetupChecklistVariant(userData, [ONBOARDING_PAYMENTS_SETUP_CHECKLIST_VARIANTS.VARIANT_2, ONBOARDING_PAYMENTS_SETUP_CHECKLIST_VARIANTS.VARIANT_3])) {
    return null;
  }
  return <OnboardingPaymentsSetupChecklistBody onEnableEventTypePayment={onEnableEventTypePayment} useConnectedIntegrationsQuery={useConnectedIntegrationsQuery} userData={userData} />;
};
const OnboardingPaymentsSetupChecklistBody = ({
  onEnableEventTypePayment,
  useConnectedIntegrationsQuery,
  userData
}) => {
  const dispatch = useDispatch();
  const track = useTrack();
  const isPaypalPpcpFeatureVariation = useIsPaypalPpcpFeatureVariation();
  const experimentState = getOnboardingPaymentsSetupChecklistExperimentState(userData);
  const [updateGrowthExperiments] = useUpdateUserGrowthExperimentsMutation();
  const {
    data: integrationsData,
    isLoading: isIntegrationsLoading
  } = useConnectedIntegrationsQuery();
  const {
    data: eventTypesData,
    isLoading: isEventTypesLoading
  } = useGetEventTypesCollectionQuery({
    scope: 'my_calendly',
    skipPagination: true
  });
  const {
    data: paidBookingStatusData,
    isLoading: isPaidBookingStatusLoading
  } = useGetPaidBookingStatusQuery(userData.id, {
    skip: !userData.id
  });
  const eventTypes = useMemo(() => getEventTypes(eventTypesData), [eventTypesData]);
  const connectedPaymentProvider = getConnectedPaymentProvider(integrationsData);
  const hasPaidEventType = eventTypes.some(eventType => isManageablePaymentEventType(eventType) && getPaymentEnabled(eventType));
  const progressState = getProgressState({
    connectedPaymentProvider,
    hasPaidEventType,
    hasSharedPaidEventType: Boolean(experimentState.shared_paid_event_type_id),
    hasSuccessfulPaidBooking: Boolean(paidBookingStatusData?.has_successful_paid_booking)
  });
  const shouldShowBanner = getShouldShowBanner(experimentState, progressState);
  const isProgressLoading = isIntegrationsLoading || isEventTypesLoading || isPaidBookingStatusLoading;
  const showEnableCta = progressState === PROGRESS_STATES.CONNECTED && Boolean(onEnableEventTypePayment);
  const showConnectCta = progressState === PROGRESS_STATES.NOT_CONNECTED;
  const {
    data: paypalReferralData,
    isFetching: isPaypalConnectUrlLoading
  } = useGetPaypalReferralQuery(undefined, {
    skip: isPaypalPpcpFeatureVariation || !showConnectCta || !shouldShowBanner || isProgressLoading
  });
  const paymentProviderConnectors = useMemo(() => getPaymentProviderConnectors({
    isPaypalPpcpFeatureVariation,
    isPaypalConnectUrlLoading,
    paypalConnectUrl: paypalReferralData?.connectUrl
  }), [isPaypalConnectUrlLoading, isPaypalPpcpFeatureVariation, paypalReferralData?.connectUrl]);
  useTrackOnce(shouldShowBanner && !isProgressLoading, {
    name: TRACKING_EVENT_NAMES.CTA_SHOWN,
    properties: {
      cta_name: 'payments_onboarding_setup_checklist',
      experiment_key: ONBOARDING_PAYMENTS_SETUP_CHECKLIST_EXPERIMENT_KEY,
      experiment_variant: experimentState.variant,
      progress_state: progressState
    }
  });
  useEffect(() => {
    if (isProgressLoading || !shouldShowBanner || progressState === PROGRESS_STATES.NOT_CONNECTED || experimentState.last_recorded_progress_state === progressState) return;
    track({
      name: TRACKING_EVENT_NAMES.FLOW_STEP_COMPLETED,
      properties: {
        flow_name: 'payments_onboarding_setup_checklist',
        flow_step: progressState,
        experiment_key: ONBOARDING_PAYMENTS_SETUP_CHECKLIST_EXPERIMENT_KEY,
        experiment_variant: experimentState.variant
      }
    });
    updateGrowthExperiments(getExperimentUpdate(userData, {
      last_recorded_progress_state: progressState
    }));
  }, [experimentState.last_recorded_progress_state, experimentState.variant, isProgressLoading, progressState, shouldShowBanner, track, updateGrowthExperiments, userData]);
  const dismissBanner = useCallback(() => {
    track({
      name: TRACKING_EVENT_NAMES.CTA_CLICKED,
      properties: {
        cta_name: 'payments_onboarding_setup_checklist_dismiss',
        experiment_key: ONBOARDING_PAYMENTS_SETUP_CHECKLIST_EXPERIMENT_KEY,
        experiment_variant: experimentState.variant,
        progress_state: progressState
      }
    });
    updateGrowthExperiments(getExperimentUpdate(userData, {
      banner_dismissed_at: new Date().toISOString(),
      banner_dismissed_progress_state: progressState,
      banner_dismissals_count: Number(experimentState.banner_dismissals_count || 0) + 1
    }));
  }, [experimentState.banner_dismissals_count, experimentState.variant, progressState, track, updateGrowthExperiments, userData]);
  const openPaymentEditor = useCallback(() => {
    const eventType = getFirstEditablePaymentEventType(eventTypes);
    if (!eventType) return;
    track({
      name: TRACKING_EVENT_NAMES.CTA_CLICKED,
      properties: {
        cta_name: 'payments_onboarding_setup_checklist_enable_event_type',
        experiment_key: ONBOARDING_PAYMENTS_SETUP_CHECKLIST_EXPERIMENT_KEY,
        experiment_variant: experimentState.variant,
        progress_state: progressState
      }
    });
    onEnableEventTypePayment?.({
      eventType,
      track
    });
  }, [eventTypes, experimentState.variant, onEnableEventTypePayment, progressState, track]);
  const connectPaymentProvider = useCallback(providerConnector => {
    track({
      name: TRACKING_EVENT_NAMES.CTA_CLICKED,
      properties: {
        cta_name: providerConnector.ctaName,
        experiment_key: ONBOARDING_PAYMENTS_SETUP_CHECKLIST_EXPERIMENT_KEY,
        experiment_variant: experimentState.variant,
        payment_provider: providerConnector.provider,
        progress_state: progressState
      }
    });
    if (providerConnector.authorizePath) {
      dispatch(redirectToAuthorizeUrl(providerConnector.authorizePath));
    }
  }, [dispatch, experimentState.variant, progressState, track]);
  if (isProgressLoading || !shouldShowBanner) return null;
  const hasBannerAction = showConnectCta || showEnableCta;
  return <section className={bannerCss} aria-label="Payments setup checklist">
      <div className={bannerMainCss}>
        <AtmCardIcon color={colorTextStandard} />
        <div className={bannerContentCss}>
          <BodyText size="medium" weight="bold" className={bannerTitleCss}>
            {getBannerCopy(progressState, connectedPaymentProvider)}
          </BodyText>
          <ProgressSteps progressState={progressState} />
        </div>
      </div>
      {hasBannerAction ? <div className={bannerActionsCss}>
          {showConnectCta ? paymentProviderConnectors.map(providerConnector => {
        const linkProps = providerConnector.href ? {
          as: 'a',
          href: providerConnector.href
        } : {};
        return <Button key={providerConnector.provider} decoration="secondary-outline" loading={providerConnector.isLoading} onClick={() => connectPaymentProvider(providerConnector)} {...linkProps}>
                <BrandLogo className={providerLogoCss} name={providerConnector.logoName} />
                Connect
                {' '}
                {providerConnector.label}
              </Button>;
      }) : null}
          {showEnableCta ? <Button decoration="secondary-outline" onClick={openPaymentEditor}>
              Enable in Event Type editor
            </Button> : null}
        </div> : null}
      <BareButton className={closeButtonCss} onClick={dismissBanner} aria-label="Dismiss payments setup checklist">
        <CloseMiniIcon />
      </BareButton>
    </section>;
};

require("./onboarding_payments_setup_checklist.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./onboarding_payments_setup_checklist.js");